import {
  Box3,
  BufferGeometry,
  Frustum,
  Group,
  Matrix4,
  Mesh,
  PerspectiveCamera,
  Vector3,
  type MeshStandardMaterial,
} from 'three';
import { CHUNKS_PER_SIDE, LOD_DISTANCES, LOD_SEGMENTS, RENDER, WORLD } from '../config';
import { buildChunkGeometry } from './chunkGeometry';
import { HeightField } from './HeightField';
import { createTerrainMaterial, type TerrainMaterial } from './terrainMaterial';
import type { TerrainTextureSet } from './TerrainTextures';

interface Chunk {
  mesh: Mesh;
  center: Vector3;
  box: Box3;
  /** Poloměr v XZ, kvůli měření vzdálenosti od okraje chunku, ne od středu. */
  radius: number;
  lod: number;
  castsShadow: boolean;
}

export interface TerrainStats {
  chunksTotal: number;
  chunksVisible: number;
  triangles: number;
  lodHistogram: number[];
}

/**
 * Dlaždicový terén s LOD a vlastním frustum cullingem.
 *
 * Klíčové rozhodnutí: geometrie je sdílená mezi všemi chunky (5 bufferů celkem),
 * protože výšky přidává až vertex shader. Chunk je tedy jen Mesh s pozicí a
 * odkazem na jednu z pěti geometrií — LOD switch nestojí prakticky nic
 * a nevzniká GC tlak při pohybu po světě.
 *
 * Frustum culling si děláme sami, protože sdílená geometrie nemůže mít
 * bounding box konkrétního chunku. Vlastní Box3 z reálného min/max výšky
 * je navíc mnohem těsnější než cokoliv, co by three odvodilo automaticky.
 */
export class Terrain {
  readonly group = new Group();
  readonly material: TerrainMaterial;
  readonly heightField: HeightField;
  readonly stats: TerrainStats;

  private readonly lodGeometries: BufferGeometry[];
  private readonly lodTriangles: number[];
  private readonly chunks: Chunk[] = [];
  private readonly frustum = new Frustum();
  private readonly viewProjection = new Matrix4();
  private readonly lastLodOrigin = new Vector3(Infinity, Infinity, Infinity);

  constructor(
    heightField: HeightField,
    textures: TerrainTextureSet,
    setupShadows?: (m: MeshStandardMaterial) => void,
  ) {
    this.heightField = heightField;
    this.group.name = 'terrain';
    this.material = createTerrainMaterial(heightField.texture, textures, setupShadows);

    this.lodGeometries = LOD_SEGMENTS.map((segments) => buildChunkGeometry(segments, WORLD.chunkSize));
    this.lodTriangles = this.lodGeometries.map((g) => (g.getIndex()?.count ?? 0) / 3);

    const half = WORLD.size * 0.5;
    for (let cz = 0; cz < CHUNKS_PER_SIDE; cz++) {
      for (let cx = 0; cx < CHUNKS_PER_SIDE; cx++) {
        const x0 = cx * WORLD.chunkSize - half;
        const z0 = cz * WORLD.chunkSize - half;
        const x1 = x0 + WORLD.chunkSize;
        const z1 = z0 + WORLD.chunkSize;
        const [minY, maxY] = heightField.minMaxIn(x0, z0, x1, z1);

        const mesh = new Mesh(this.lodGeometries[0], this.material);
        mesh.position.set(x0, 0, z0);
        mesh.receiveShadow = true;
        mesh.castShadow = false;
        // Culling řešíme ručně níž v update().
        mesh.frustumCulled = false;
        mesh.matrixAutoUpdate = false;
        mesh.updateMatrix();
        mesh.updateMatrixWorld(true);
        mesh.visible = false;
        this.group.add(mesh);

        // Sukně visí až 120 m dolů, box to musí obsáhnout, jinak by se chunk
        // u okraje obrazu ořízl dřív, než zmizí z pohledu.
        this.chunks.push({
          mesh,
          center: new Vector3((x0 + x1) * 0.5, (minY + maxY) * 0.5, (z0 + z1) * 0.5),
          box: new Box3(new Vector3(x0, minY - 130, z0), new Vector3(x1, maxY, z1)),
          radius: WORLD.chunkSize * Math.SQRT1_2,
          lod: -1,
          castsShadow: false,
        });
      }
    }

    this.stats = {
      chunksTotal: this.chunks.length,
      chunksVisible: 0,
      triangles: 0,
      lodHistogram: new Array(LOD_SEGMENTS.length).fill(0),
    };
  }

  /** Volat jednou za snímek, po aktualizaci kamery. */
  update(camera: PerspectiveCamera): void {
    // LOD přepočítáváme jen když se kamera opravdu pohnula — nemá smysl to
    // dělat 60x za sekundu při stání na místě.
    const moved = this.lastLodOrigin.distanceToSquared(camera.position) > 16;
    if (moved) {
      this.lastLodOrigin.copy(camera.position);
      this.updateLods(camera);
    }

    this.viewProjection.multiplyMatrices(camera.projectionMatrix, camera.matrixWorldInverse);
    this.frustum.setFromProjectionMatrix(this.viewProjection);

    let visible = 0;
    let triangles = 0;
    const histogram = this.stats.lodHistogram;
    histogram.fill(0);

    for (const chunk of this.chunks) {
      const dx = chunk.center.x - camera.position.x;
      const dz = chunk.center.z - camera.position.z;
      const distance = Math.sqrt(dx * dx + dz * dz) - chunk.radius;

      const inRange = distance <= RENDER.viewDistance;
      const show = inRange && this.frustum.intersectsBox(chunk.box);

      if (chunk.mesh.visible !== show) chunk.mesh.visible = show;
      if (!show) continue;

      visible++;
      triangles += this.lodTriangles[chunk.lod];
      histogram[chunk.lod]++;
    }

    this.stats.chunksVisible = visible;
    this.stats.triangles = triangles;
  }

  private updateLods(camera: PerspectiveCamera): void {
    for (const chunk of this.chunks) {
      const dx = chunk.center.x - camera.position.x;
      const dz = chunk.center.z - camera.position.z;
      const distance = Math.sqrt(dx * dx + dz * dz) - chunk.radius;

      let lod = LOD_SEGMENTS.length - 1;
      for (let i = 0; i < LOD_DISTANCES.length; i++) {
        if (distance < LOD_DISTANCES[i]) {
          lod = i;
          break;
        }
      }

      if (chunk.lod !== lod) {
        chunk.lod = lod;
        chunk.mesh.geometry = this.lodGeometries[lod];
      }

      // Stíny vrhá jen blízké okolí. Vzdálené hory by stály celou shadow pass
      // a v kaskádách CSM by stejně nebyly vidět.
      const shouldCast = distance <= RENDER.shadowCastDistance;
      if (chunk.castsShadow !== shouldCast) {
        chunk.castsShadow = shouldCast;
        chunk.mesh.castShadow = shouldCast;
      }
    }
  }

  dispose(): void {
    for (const geometry of this.lodGeometries) geometry.dispose();
    this.material.dispose();
    this.heightField.dispose();
  }
}
